import {
  OrderType,
  QuestionGroupType,
  useSearchItemSetQuestionsInLocationQuery,
} from '@exam-management/graphql';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { Flex, Text, PaperSection } from '@shared/components';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { NoQuestionInLocationOverlay } from '../../components';
import { getUniqueQuestions, type ItemSetCreationFormInput } from '../../utils';
import ItemSetQuestionsInLocationList from '../item-set-questions-in-location-section/item-set-questions-in-location-list';

const PAGE_SIZE = 10;

export default function FormItemSetQuestionsInLocationSection() {
  const { qBankStructureInformation } = useQBankStructure();
  const formContext = useFormContext<ItemSetCreationFormInput>();
  const { watch } = formContext;
  const location = watch('location');
  const { SUB_TOPIC } = location;
  const subTopicLevelItemId = SUB_TOPIC?.value;

  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    setCurrentPage(0);
  }, [SUB_TOPIC]);

  const {
    data: itemSetInThisLocation,
    loading: isLoadingItemSetInThisLocation,
    fetchMore: fetchMoreItemSetInThisLocation,
  } = useSearchItemSetQuestionsInLocationQuery({
    variables: {
      search: {
        levelItemId: subTopicLevelItemId,
        refQBankStructureId: qBankStructureInformation.id,
        questionGroupType: [QuestionGroupType.ItemSet],
      },
      paging: {
        pageSize: PAGE_SIZE,
        currentPage: 0,
      },
      order: {
        field: 'createdAt',
        type: OrderType.Asc,
      },
    },
    skip: subTopicLevelItemId === undefined,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const handleFetchMore = async () => {
    await fetchMoreItemSetInThisLocation({
      variables: {
        paging: {
          pageSize: PAGE_SIZE,
          currentPage: currentPage + 1,
        },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;

        const uniqueQuestions = getUniqueQuestions([
          ...previousResult.qctSearchQuestionGroups.questionGroups,
          ...fetchMoreResult.qctSearchQuestionGroups.questionGroups,
        ]);

        return {
          qctSearchQuestionGroups: {
            ...previousResult.qctSearchQuestionGroups,
            questionGroups: uniqueQuestions,
          },
        };
      },
    });
    setCurrentPage((prev) => prev + 1);
  };

  const itemSetQuestions = itemSetInThisLocation?.qctSearchQuestionGroups.questionGroups ?? [];
  const totalItemSetQuestions = itemSetInThisLocation?.qctSearchQuestionGroups.total ?? 0;

  return (
    <PaperSection title='Q in this Location'>
      <Flex
        sx={(theme) => ({
          p: theme.spacing(4, 6, 6),
          flexDirection: 'column',
          gap: theme.spacing(2),
        })}
      >
        <Flex sx={{ justifyContent: 'space-between' }}>
          <Text variant='body2Semibold'>QID</Text>
          <Text variant='body2Semibold'>{totalItemSetQuestions} Qty.</Text>
        </Flex>
        {!subTopicLevelItemId ? (
          <NoQuestionInLocationOverlay variant='no-location-selected' />
        ) : (
          <ItemSetQuestionsInLocationList
            itemSetQuestions={itemSetQuestions}
            loading={isLoadingItemSetInThisLocation}
            hasMore={itemSetQuestions.length < totalItemSetQuestions}
            structureId={qBankStructureInformation.id}
            handleFetchMore={handleFetchMore}
            currentPage={currentPage}
          />
        )}
      </Flex>
    </PaperSection>
  );
}
